
import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import { useAdminStore } from './stores/admin';
import { useSessionsStore } from './stores/sessions';
import { useClassesStore } from './stores/classes';
import { useArmsStore } from './stores/arms';
import { useFeeTypesStore } from "./stores/fee-types";
import { usePaymentChannelsStore } from "./stores/payment-channels";
import { useSubjectsStore } from "./stores/subjects";
import { useAssessmentTypesStore } from "./stores/assessment-type";
import { useGradingScalesStore } from "./stores/grading-scales";
import { useSettingStore } from './stores/settings';

import { createPinia } from 'pinia'

/* import the fontawesome core */
import { library } from '@fortawesome/fontawesome-svg-core'

/* import font awesome icon component */
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

/* import specific icons */
import { faNavicon, faGaugeHigh, faSpinner, faSearch, faUser, faTrash, faArrowRightLong, faXmark, faArrowLeftLong, faCircleCheck, faCamera, faChevronLeft, faChevronRight, faBoxOpen, faUserTie, faEnvelope, faSignOut, faGauge, faUsers, faCalculator, faSort, faLocation, faMessage, faChartLine, faUserCog, faMobilePhone, faPlus, faMobileScreenButton, faMoneyBillTransfer, faArrowUp, faArrowDown, faHourglass, faTriangleExclamation, faUserCheck, faLayerGroup, faChevronDown, faChevronUp, faIdCard, faBroadcastTower, faGear, faGift, faRotate, faWallet, faEllipsisVertical, faEraser, faRightToBracket, faArrowsSpin, faShareNodes, faArrowTrendUp, faEarthAfrica, faCalendarDays, faSignal, faMagnifyingGlassPlus, faGavel, faRotateLeft, faCircleXmark, faSchool, faCalendarCheck, faPeopleRoof, faClockRotateLeft, faIdBadge, faVenusMars, faCakeCandles, faCalendarPlus, faUserShield, faPhone, faLocationDot, faPen, faCheck, faMoneyBillWave, faStar, faUserGraduate, faChalkboardTeacher, faSackDollar, faCircleExclamation, faUserPlus, faCashRegister, faPrint, faCircleInfo, faCircleQuestion, faDroplet, faPlaceOfWorship, faMap, faLocationCrosshairs, faBaby, faHouse, faBuildingColumns, faUserDoctor, faHospital, faChartPie, faPenToSquare, faUserSlash, faChalkboardUser, faGraduationCap, faLock, faKey, faBullhorn, faEye, faList, faFileExcel, faFileImport, faDownload, faImages, faClipboardCheck, faCloudArrowUp, faFileZipper, faCirclePlay, faRocket, faCode, faGlobe, faArrowLeft } from '@fortawesome/free-solid-svg-icons'

import { faClock, faCalendar } from '@fortawesome/free-regular-svg-icons'

import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'

/* add icons to the library */
library.add( faNavicon, faGaugeHigh, faSpinner, faSearch, faUser, faTrash, faArrowRightLong, faXmark, faArrowLeftLong, faCircleCheck, faCamera, faChevronLeft, faChevronRight, faBoxOpen, faUserTie, faEnvelope, faSignOut, faGauge, faUsers, faCalculator, faSort, faLocation, faMessage, faChartLine, faUserCog, faMobilePhone, faPlus, faMobileScreenButton, faMoneyBillTransfer, faArrowUp, faArrowDown, faHourglass, faTriangleExclamation, faUserCheck, faLayerGroup, faChevronDown, faChevronUp, faIdCard, faBroadcastTower, faGear, faGift, faRotate, faWallet, faEllipsisVertical, faEraser, faRightToBracket, faArrowsSpin, faShareNodes, faArrowTrendUp, faEarthAfrica, faCalendarDays, faSignal, faMagnifyingGlassPlus, faGavel, faRotateLeft, faCircleXmark, faSchool, faCalendarCheck, faPeopleRoof, faClockRotateLeft, faIdBadge, faVenusMars, faCakeCandles, faCalendarPlus, faUserShield, faPhone, faLocationDot, faPen, faCheck, faMoneyBillWave, faStar, faUserGraduate, faChalkboardTeacher, faSackDollar, faCircleExclamation, faUserPlus, faCashRegister, faPrint, faCircleInfo, faCircleQuestion, faDroplet, faPlaceOfWorship, faMap, faLocationCrosshairs, faBaby, faHouse, faBuildingColumns, faUserDoctor, faHospital, faChartPie, faPenToSquare, faUserSlash, faChalkboardUser, faGraduationCap, faLock, faKey, faBullhorn, faEye, faList, faFileExcel, faFileImport, faDownload, faImages, faClipboardCheck, faCloudArrowUp, faFileZipper, faCirclePlay, faRocket, faCode, faGlobe, faArrowLeft, faWhatsapp )


// Import the component
import SUCCESSALERTBOX from './components/alert_box/success.vue';
import ERRORALERTBOX from './components/alert_box/error.vue';
import ACTIONMODAL from "./components/modals/ActionModal.vue";
import OVERLAY from "./components/modals/loading_overlay.vue"
const app = createApp(App)

const pinia = createPinia()

app.component('font-awesome-icon', FontAwesomeIcon)


// Register the component globally
app.component('SUCCESSALERTBOX', SUCCESSALERTBOX);
app.component('ERRORALERTBOX', ERRORALERTBOX);
app.component('ACTIONMODAL', ACTIONMODAL);
app.component('OVERLAY', OVERLAY);

app.use(router)
app.use(pinia)

// Fetch data before mounting the app
const initializeApp = async () => {
    
    const admin_store = useAdminStore(); // Access the admin

    const sessions_store = useSessionsStore()

    const fee_types_store = useFeeTypesStore()

    const channels_store = usePaymentChannelsStore()

    const subjects_store = useSubjectsStore()

    const grading_scales_store = useGradingScalesStore()

    const assessment_types_store = useAssessmentTypesStore()

    const classes_store = useClassesStore()

    const arms_store = useArmsStore()

    const settings_store = useSettingStore()
    
    try {

        await admin_store.fetch_admin().catch(err => { // Try to fetch user but DON'T block the app if it fails

            console.log("Admin fetch failed (not logged in).", err); 
          
        });

        if (admin_store.isAuthenticated) {

            await Promise.all([

            sessions_store.fetch_sessions(),

            classes_store.fetch_classes(),

            fee_types_store.fetch_fee_types(),

            channels_store.fetch_channels(),

            subjects_store.fetch_subjects(),

            grading_scales_store.fetch_grading_scales(),

            assessment_types_store.fetch_assessment_types(),

            arms_store.fetch_arms(),

            settings_store.fetch_settings()
    
            ]);
        }       
    
    } catch (error) {
    
        console.log("Error during initialization:", error);

    }
  
    app.mount('#app'); // Mount the app only after data is fetched
  
};
  
initializeApp();
